import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Input,
  Button,
  Collapse,
  Spin,
  Alert,
  Modal,
  message,
  DatePicker,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useAuth } from '../../../contexts/AuthContext';
import {
  AirplaneArrivalIcon,
  AirplaneDepartureIcon,
} from '../../../components/icons/TripIcons';
import {
  createTrip,
  syncTripToDevice,
  getVisibleTrips,
  deleteTrip,
} from '../../../services/tripsService';
import {
  listCruiseLines,
  listAllItinerariesForLine,
  getItinerary,
  distinctSubBrands,
  filterSailings,
  groupSailingsByMonth,
  monthsByYear,
  monthSummaryLabel,
  sailingDisplaySummaryFromRow,
  sailingDisplaySummaryFromDetail,
  sailingDateAndPortLine,
  sailingShipLine,
  sailingPortRouteLine,
  formatEmbarkDateShort,
  formatEmbarkDateMedium,
  defaultTripName,
  addDaysToIsoDate,
  diffIsoCalendarDays,
  cruisePortPreviewRows,
  type CruiseLineCatalogEntry,
  type CruiseEmbarkationRow,
  type CruiseItineraryDetail,
} from '../../../services/cruiseItinerariesApi';
import {
  mapCruiseItineraryToTrip,
  mappedImportToTripData,
  type CruiseImportMappedTrip,
} from '../../../services/cruiseItineraryMapper';
import { startPlanJobs } from '../../../services/aiPlanJobService';
import { addTripEn, tripDetailEn } from '../../../content/trips.en';
import { findFingerprintMatches } from '../../../utils/tripDedup';
import type { Trip } from '../../../types/client';
import { CruiseLineLogo, CruiseSubBrandLogo } from '../../../components/CruiseLineLogo/CruiseLineLogo';
import CruisePortStopPreview from '../../../components/CruisePortStopPreview/CruisePortStopPreview';
import portPreviewStyles from '../../../components/CruisePortStopPreview/CruisePortStopPreview.module.css';
import styles from './AddTrip.module.css';

type SectionKey = 'cruiseLine' | 'subBrand' | 'date' | 'sailings' | 'travelDates' | 'ports';

const AddTrip: React.FC = () => {
  const navigate = useNavigate();
  const { user, isDataSyncing } = useAuth();

  const [catalog, setCatalog] = useState<CruiseLineCatalogEntry[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [selectedCruiseLine, setSelectedCruiseLine] = useState<string | null>(null);
  const [selectedSubBrand, setSelectedSubBrand] = useState<string | null>(null);
  const [selectedMonthKey, setSelectedMonthKey] = useState<string | null>(null);
  const [hasSelectedDateRange, setHasSelectedDateRange] = useState(false);
  const [includedShipFilters, setIncludedShipFilters] = useState<Set<string>>(new Set());
  const [includedSubBrandFilters, setIncludedSubBrandFilters] = useState<Set<string>>(new Set());

  const [sailings, setSailings] = useState<CruiseEmbarkationRow[]>([]);
  const [sailingsLoading, setSailingsLoading] = useState(false);
  const [sailingsError, setSailingsError] = useState<string | null>(null);

  const [selectedItineraryId, setSelectedItineraryId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CruiseItineraryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mapped, setMapped] = useState<CruiseImportMappedTrip | null>(null);
  const [tripName, setTripName] = useState('');
  const [coreEmbarkDate, setCoreEmbarkDate] = useState<string | null>(null);
  const [coreDebarkDate, setCoreDebarkDate] = useState<string | null>(null);
  const [embarkArrivalDate, setEmbarkArrivalDate] = useState<string | null>(null);
  const [debarkDepartureDate, setDebarkDepartureDate] = useState<string | null>(null);
  const [daysBefore, setDaysBefore] = useState(0);
  const [daysAfter, setDaysAfter] = useState(0);

  const [activeKeys, setActiveKeys] = useState<string[]>(['cruiseLine']);
  const [importing, setImporting] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<Trip[]>([]);
  const [addAsNewConfirmOpen, setAddAsNewConfirmOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      setCatalogError(null);
      try {
        const lines = await listCruiseLines();
        if (!cancelled) {
          setCatalog(lines.sort((a, b) => a.cruiseLine.localeCompare(b.cruiseLine)));
        }
      } catch (err) {
        if (!cancelled) {
          setCatalogError(err instanceof Error ? err.message : 'Failed to load cruise lines.');
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCatalogEntry = useMemo(
    () => catalog.find((e) => e.cruiseLine === selectedCruiseLine) ?? null,
    [catalog, selectedCruiseLine]
  );

  const subBrands = useMemo(
    () => (selectedCatalogEntry ? distinctSubBrands(selectedCatalogEntry) : []),
    [selectedCatalogEntry]
  );

  const showsSubBrandSection = subBrands.length > 1;

  const filteredSailings = useMemo(
    () => filterSailings(sailings, includedShipFilters, includedSubBrandFilters),
    [sailings, includedShipFilters, includedSubBrandFilters]
  );

  const shipOptions = useMemo(() => {
    const names = new Set(sailings.map((s) => s.shipName).filter(Boolean));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [sailings]);

  const sailingGroups = useMemo(() => groupSailingsByMonth(filteredSailings), [filteredSailings]);

  const monthGroups = useMemo(() => monthsByYear(), []);

  const rebuildMapped = useCallback(
    async (
      nextDetail: CruiseItineraryDetail,
      name: string,
      before: number,
      after: number
    ): Promise<CruiseImportMappedTrip | null> => {
      try {
        const result = await mapCruiseItineraryToTrip(nextDetail, {
          tripName: name,
          daysBeforeCruise: before,
          daysAfterCruise: after,
        });
        setMapped(result);
        return result;
      } catch (err) {
        message.error(err instanceof Error ? err.message : 'Failed to map cruise.');
        setMapped(null);
        return null;
      }
    },
    []
  );

  const loadSailings = useCallback(async (line: string, monthKey: string | null) => {
    setSailingsLoading(true);
    setSailingsError(null);
    try {
      const rows = await listAllItinerariesForLine(line, monthKey);
      setSailings(rows);
    } catch (err) {
      setSailings([]);
      setSailingsError(err instanceof Error ? err.message : 'Failed to load sailings.');
    } finally {
      setSailingsLoading(false);
    }
  }, []);

  const clearPrePostCalendar = (): void => {
    setCoreEmbarkDate(null);
    setCoreDebarkDate(null);
    setEmbarkArrivalDate(null);
    setDebarkDepartureDate(null);
    setDaysBefore(0);
    setDaysAfter(0);
  };

  const selectCruiseLine = (line: string): void => {
    setSelectedCruiseLine(line);
    setSelectedSubBrand(null);
    setIncludedSubBrandFilters(new Set());
    setIncludedShipFilters(new Set());
    setSelectedMonthKey(null);
    setHasSelectedDateRange(false);
    setSailings([]);
    setSailingsError(null);
    setSelectedItineraryId(null);
    setDetail(null);
    setMapped(null);
    setTripName('');
    clearPrePostCalendar();
    setActiveKeys(showsSubBrandForLine(line) ? ['subBrand'] : ['date']);
  };

  const showsSubBrandForLine = (line: string): boolean => {
    const entry = catalog.find((e) => e.cruiseLine === line);
    return entry ? distinctSubBrands(entry).length > 1 : false;
  };

  const selectSubBrand = (brand: string | null): void => {
    setSelectedSubBrand(brand);
    setIncludedSubBrandFilters(brand ? new Set([brand]) : new Set());
    setSelectedItineraryId(null);
    setDetail(null);
    setMapped(null);
    clearPrePostCalendar();
    setActiveKeys(['date']);
  };

  const selectDateRange = (monthKey: string | null): void => {
    if (!selectedCruiseLine) return;
    setSelectedMonthKey(monthKey);
    setHasSelectedDateRange(true);
    setSelectedItineraryId(null);
    setDetail(null);
    setMapped(null);
    clearPrePostCalendar();
    setActiveKeys(['sailings']);
    void loadSailings(selectedCruiseLine, monthKey);
  };

  const selectSailing = async (row: CruiseEmbarkationRow): Promise<void> => {
    setSelectedItineraryId(row.itineraryId);
    setDetailLoading(true);
    setDaysBefore(0);
    setDaysAfter(0);
    try {
      const loaded = await getItinerary(row.itineraryId);
      setDetail(loaded);
      const name = defaultTripName(loaded);
      setTripName(name);
      const result = await rebuildMapped(loaded, name, 0, 0);
      if (result) {
        setCoreEmbarkDate(result.startDate);
        setCoreDebarkDate(result.endDate);
        setEmbarkArrivalDate(result.startDate);
        setDebarkDepartureDate(result.endDate);
      }
      setActiveKeys(['travelDates', 'ports']);
    } catch (err) {
      setDetail(null);
      setMapped(null);
      setCoreEmbarkDate(null);
      setCoreDebarkDate(null);
      setEmbarkArrivalDate(null);
      setDebarkDepartureDate(null);
      message.error(err instanceof Error ? err.message : 'Failed to load itinerary.');
    } finally {
      setDetailLoading(false);
    }
  };

  const applyPrePostDates = (arrivalIso: string, departureIso: string): void => {
    if (!coreEmbarkDate || !coreDebarkDate) return;
    const before = Math.max(0, Math.min(14, diffIsoCalendarDays(arrivalIso, coreEmbarkDate)));
    const after = Math.max(0, Math.min(14, diffIsoCalendarDays(coreDebarkDate, departureIso)));
    const clampedArrival = addDaysToIsoDate(coreEmbarkDate, -before);
    const clampedDeparture = addDaysToIsoDate(coreDebarkDate, after);
    setEmbarkArrivalDate(clampedArrival);
    setDebarkDepartureDate(clampedDeparture);
    setDaysBefore(before);
    setDaysAfter(after);
  };

  const onEmbarkArrivalChange = (value: Dayjs | null): void => {
    if (!value || !debarkDepartureDate) return;
    applyPrePostDates(value.format('YYYY-MM-DD'), debarkDepartureDate);
  };

  const onDebarkDepartureChange = (value: Dayjs | null): void => {
    if (!value || !embarkArrivalDate) return;
    applyPrePostDates(embarkArrivalDate, value.format('YYYY-MM-DD'));
  };

  useEffect(() => {
    if (!detail) return;
    void rebuildMapped(detail, tripName, daysBefore, daysAfter);
    // tripName is applied at import time; remapping only when dates/detail change
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid regenerating city ids while typing title
  }, [daysBefore, daysAfter, detail, rebuildMapped]);

  useEffect(() => {
    setMapped((prev) => (prev ? { ...prev, tripName: tripName.trim() || prev.tripName } : prev));
  }, [tripName]);

  const findOverlapping = (
    startDate: string,
    endDate: string,
    title: string,
    itineraryId?: string | null
  ): Trip[] => {
    if (!user?.sub) return [];
    return findFingerprintMatches(getVisibleTrips(user.sub), {
      title,
      tripType: 'cruise',
      startDate,
      endDate,
      sourceItineraryId: itineraryId ?? null,
    });
  };

  const performImport = async (replaceOverlapping: boolean): Promise<void> => {
    if (!user?.sub || !mapped) return;
    setImporting(true);
    try {
      const finalMapped = await mapCruiseItineraryToTrip(detail!, {
        tripName,
        daysBeforeCruise: daysBefore,
        daysAfterCruise: daysAfter,
      });
      if (replaceOverlapping) {
        const overlapping = findOverlapping(
          finalMapped.startDate,
          finalMapped.endDate,
          finalMapped.tripName,
          finalMapped.itineraryId
        );
        for (const trip of overlapping) {
          deleteTrip(user.sub, trip.id);
        }
      }
      const created = createTrip(user.sub, mappedImportToTripData(finalMapped), {
        deferDeviceSync: true,
      });
      try {
        await syncTripToDevice(created, { userId: user.sub });
      } catch (syncErr) {
        console.warn('AddTrip: device sync after import failed', syncErr);
        message.warning('Trip saved locally; cloud sync will retry later.');
      }
      startPlanJobs(created);
      message.success(addTripEn.tripCreated);
      navigate(`/trips/${created.id}`);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setImporting(false);
      setDuplicateOpen(false);
      setAddAsNewConfirmOpen(false);
    }
  };

  const attemptImport = async (): Promise<void> => {
    if (!user?.sub || !mapped || !detail) return;
    const name = tripName.trim();
    if (!name) {
      message.error(addTripEn.titleRequired);
      return;
    }
    const finalMapped = await mapCruiseItineraryToTrip(detail, {
      tripName: name,
      daysBeforeCruise: daysBefore,
      daysAfterCruise: daysAfter,
    });
    const overlapping = findOverlapping(
      finalMapped.startDate,
      finalMapped.endDate,
      finalMapped.tripName,
      finalMapped.itineraryId
    );
    if (overlapping.length > 0) {
      setDuplicateMatches(overlapping);
      setDuplicateOpen(true);
      return;
    }
    await performImport(false);
  };

  const openExistingDuplicate = (): void => {
    const best = duplicateMatches[0];
    if (!best) return;
    setDuplicateOpen(false);
    navigate(`/trips/${best.id}`);
  };

  const canImport =
    Boolean(mapped && detail && tripName.trim() && mapped.cityIds.length > 0) &&
    !detailLoading &&
    !importing;

  const handleEditBeforeSaving = (): void => {
    if (!detail || !mapped) return;
    void (async () => {
      try {
        const finalMapped = await mapCruiseItineraryToTrip(detail, {
          tripName: tripName.trim() || mapped.tripName,
          daysBeforeCruise: daysBefore,
          daysAfterCruise: daysAfter,
        });
        navigate('/trips/new/edit', {
          state: { prefill: mappedImportToTripData(finalMapped) },
        });
      } catch (err) {
        message.error(err instanceof Error ? err.message : 'Failed to map cruise.');
      }
    })();
  };

  const selectedSummary = selectedItineraryId
    ? detail
      ? sailingDisplaySummaryFromDetail(detail)
      : sailings.find((s) => s.itineraryId === selectedItineraryId)
        ? sailingDisplaySummaryFromRow(
            sailings.find((s) => s.itineraryId === selectedItineraryId)!
          )
        : null
    : null;

  const portPreviewRows = useMemo(
    () =>
      cruisePortPreviewRows(detail?.ports, {
        timeRange: addTripEn.portTimeRange,
        arrivalOnly: addTripEn.portArrivalOnly,
        departureOnly: addTripEn.portDepartureOnly,
      }),
    [detail]
  );

  const collapseItems = [
    {
      key: 'cruiseLine',
      label: (
        <SectionLabel
          title={selectedCruiseLine ? addTripEn.cruiseLineSelected : addTripEn.findCruiseLine}
          summary={selectedCruiseLine ?? undefined}
        />
      ),
      children: catalogLoading ? (
        <div className={styles.loadingRow}>
          <Spin size="small" /> {addTripEn.loadingCruiseLines}
        </div>
      ) : catalogError ? (
        <Alert type="error" message={catalogError} showIcon />
      ) : catalog.length === 0 ? (
        <div className={styles.muted}>{addTripEn.noCruiseLines}</div>
      ) : (
        <div className={styles.choiceList}>
          {catalog.map((entry) => (
            <button
              key={entry.cruiseLine}
              type="button"
              className={`${styles.choiceRow} ${
                selectedCruiseLine === entry.cruiseLine ? styles.choiceRowSelected : ''
              }`}
              onClick={() => selectCruiseLine(entry.cruiseLine)}
            >
              <CruiseLineLogo cruiseLine={entry.cruiseLine} size="list" />
              <span>{entry.cruiseLine}</span>
            </button>
          ))}
        </div>
      ),
    },
    ...(showsSubBrandSection
      ? [
          {
            key: 'subBrand' as SectionKey,
            label: (
              <SectionLabel
                title={addTripEn.subBrand}
                summary={selectedSubBrand ?? addTripEn.allSubBrands}
              />
            ),
            children: (
              <div className={styles.choiceList}>
                <button
                  type="button"
                  className={`${styles.choiceRow} ${
                    selectedSubBrand == null ? styles.choiceRowSelected : ''
                  }`}
                  onClick={() => selectSubBrand(null)}
                >
                  {addTripEn.allSubBrands}
                </button>
                {subBrands.map((brand) => (
                  <button
                    key={brand}
                    type="button"
                    className={`${styles.choiceRow} ${
                      selectedSubBrand === brand ? styles.choiceRowSelected : ''
                    }`}
                    onClick={() => selectSubBrand(brand)}
                  >
                    <CruiseSubBrandLogo label={brand} />
                    <span>{brand}</span>
                  </button>
                ))}
              </div>
            ),
          },
        ]
      : []),
    {
      key: 'date',
      label: (
        <SectionLabel
          title={addTripEn.dateRange}
          summary={hasSelectedDateRange ? monthSummaryLabel(selectedMonthKey) : undefined}
        />
      ),
      children: !selectedCruiseLine ? (
        <div className={styles.muted}>{addTripEn.findCruiseLine}</div>
      ) : (
        <div className={styles.monthGrid}>
          <button
            type="button"
            className={`${styles.monthChip} ${
              hasSelectedDateRange && selectedMonthKey == null ? styles.monthChipSelected : ''
            }`}
            onClick={() => selectDateRange(null)}
          >
            {addTripEn.allDates}
          </button>
          {monthGroups.map((group) => (
            <div key={group.year} className={styles.yearBlock}>
              <div className={styles.yearLabel}>{group.year}</div>
              <div className={styles.monthChips}>
                {group.months.map((month) => (
                  <button
                    key={month.key}
                    type="button"
                    className={`${styles.monthChip} ${
                      selectedMonthKey === month.key ? styles.monthChipSelected : ''
                    }`}
                    onClick={() => selectDateRange(month.key)}
                  >
                    {month.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'sailings',
      label: (
        <SectionLabel
          title={addTripEn.sailings}
          summary={
            selectedSummary
              ? `${formatEmbarkDateShort(selectedSummary.embarkDate)} – ${formatEmbarkDateShort(selectedSummary.debarkDate)}`
              : hasSelectedDateRange && !sailingsLoading
                ? addTripEn.sailingsCount(filteredSailings.length)
                : undefined
          }
        />
      ),
      children: !hasSelectedDateRange ? (
        <div className={styles.muted}>{addTripEn.selectDateFirst}</div>
      ) : sailingsLoading ? (
        <div className={styles.loadingRow}>
          <Spin size="small" /> {addTripEn.loadingSailings}
        </div>
      ) : sailingsError ? (
        <Alert type="error" message={sailingsError} showIcon />
      ) : filteredSailings.length === 0 ? (
        <div className={styles.muted}>{addTripEn.noSailings}</div>
      ) : (
        <div>
          {shipOptions.length > 1 && (
            <div className={styles.filterBlock}>
              <div className={styles.filterHeader}>
                <span>{addTripEn.shipFilters}</span>
                {includedShipFilters.size > 0 && (
                  <button
                    type="button"
                    className={styles.clearFilters}
                    onClick={() => setIncludedShipFilters(new Set())}
                  >
                    {addTripEn.clearFilters}
                  </button>
                )}
              </div>
              <div className={styles.filterChips}>
                {shipOptions.map((ship) => {
                  const selected = includedShipFilters.has(ship);
                  return (
                    <button
                      key={ship}
                      type="button"
                      className={`${styles.filterChip} ${selected ? styles.filterChipSelected : ''}`}
                      onClick={() => {
                        setIncludedShipFilters((prev) => {
                          const next = new Set(prev);
                          if (next.has(ship)) next.delete(ship);
                          else next.add(ship);
                          return next;
                        });
                      }}
                    >
                      {ship}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className={styles.sailingList}>
            {sailingGroups.map((group) => (
              <div key={group.monthKey}>
                {(selectedMonthKey == null || sailingGroups.length > 1) && (
                  <div className={styles.monthHeader}>{group.header}</div>
                )}
                {group.rows.map((row) => {
                  const summary = sailingDisplaySummaryFromRow(row);
                  const selected = selectedItineraryId === row.itineraryId;
                  const logoLine =
                    selectedCruiseLine?.trim() ||
                    row.cruiseLine.trim() ||
                    '';
                  const primary = sailingDateAndPortLine(summary, {
                    roundTrip: addTripEn.sailingRoundTrip,
                    portToPort: addTripEn.sailingPortToPort,
                  });
                  const secondary = sailingShipLine(row);
                  return (
                    <button
                      key={row.itineraryId}
                      type="button"
                      className={`${styles.sailingRow} ${selected ? styles.sailingRowSelected : ''}`}
                      onClick={() => void selectSailing(row)}
                    >
                      <CruiseLineLogo cruiseLine={logoLine} size="sailing" />
                      <div className={styles.sailingText}>
                        <div className={styles.sailingPrimary}>{primary}</div>
                        {secondary ? (
                          <div className={styles.sailingSecondary}>{secondary}</div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  if (selectedItineraryId) {
    collapseItems.push(
      {
        key: 'travelDates',
        label: (
          <SectionLabel
            title={addTripEn.travelDates}
            summary={
              mapped && embarkArrivalDate && debarkDepartureDate
                ? addTripEn.tripDateRange(
                    formatEmbarkDateMedium(embarkArrivalDate),
                    formatEmbarkDateMedium(debarkDepartureDate)
                  )
                : undefined
            }
          />
        ),
        children: detailLoading ? (
          <div className={styles.loadingRow}>
            <Spin size="small" /> {addTripEn.loadingPorts}
          </div>
        ) : !detail || !coreEmbarkDate || !coreDebarkDate ? (
          <div className={styles.muted}>{addTripEn.selectSailingFirst}</div>
        ) : (
          <div className={styles.travelDates}>
            <div className={styles.prePostBlock}>
              <div className={styles.prePostTitle}>
                <AirplaneArrivalIcon />
                <span>{addTripEn.preCruiseTitle}</span>
              </div>
              <div className={styles.prePostRow}>
                <span className={styles.prePostSubtitle}>{addTripEn.preCruiseArrival}</span>
                <DatePicker
                  className={styles.prePostDate}
                  value={embarkArrivalDate ? dayjs(embarkArrivalDate) : null}
                  allowClear={false}
                  inputReadOnly
                  format="MMM D, YYYY"
                  disabledDate={(current) => {
                    if (!current || !coreEmbarkDate) return false;
                    const min = dayjs(addDaysToIsoDate(coreEmbarkDate, -14));
                    const max = dayjs(coreEmbarkDate);
                    return current.isBefore(min, 'day') || current.isAfter(max, 'day');
                  }}
                  onChange={onEmbarkArrivalChange}
                />
              </div>
            </div>
            <div className={styles.prePostBlock}>
              <div className={styles.prePostTitle}>
                <AirplaneDepartureIcon />
                <span>{addTripEn.postCruiseTitle}</span>
              </div>
              <div className={styles.prePostRow}>
                <span className={styles.prePostSubtitle}>{addTripEn.postCruiseDeparture}</span>
                <DatePicker
                  className={styles.prePostDate}
                  value={debarkDepartureDate ? dayjs(debarkDepartureDate) : null}
                  allowClear={false}
                  inputReadOnly
                  format="MMM D, YYYY"
                  disabledDate={(current) => {
                    if (!current || !coreDebarkDate) return false;
                    const min = dayjs(coreDebarkDate);
                    const max = dayjs(addDaysToIsoDate(coreDebarkDate, 14));
                    return current.isBefore(min, 'day') || current.isAfter(max, 'day');
                  }}
                  onChange={onDebarkDepartureChange}
                />
              </div>
            </div>
            {embarkArrivalDate && debarkDepartureDate && (
              <div className={styles.muted}>
                {addTripEn.tripDateRange(
                  formatEmbarkDateMedium(embarkArrivalDate),
                  formatEmbarkDateMedium(debarkDepartureDate)
                )}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'ports',
        label: (
          <SectionLabel
            title={addTripEn.ports}
            summary={
              mapped
                ? selectedSummary
                  ? sailingPortRouteLine(selectedSummary, {
                      roundTrip: addTripEn.sailingRoundTrip,
                      portToPort: addTripEn.sailingPortToPort,
                    }) || addTripEn.portsCount(mapped.portLabels.length)
                  : addTripEn.portsCount(mapped.portLabels.length)
                : undefined
            }
          />
        ),
        children: detailLoading ? (
          <div className={styles.loadingRow}>
            <Spin size="small" /> {addTripEn.loadingPorts}
          </div>
        ) : portPreviewRows.length > 0 ? (
          <div className={portPreviewStyles.list}>
            {portPreviewRows.map((row) => (
              <CruisePortStopPreview key={row.key} row={row} />
            ))}
          </div>
        ) : (
          <div className={styles.muted}>{addTripEn.noPorts}</div>
        ),
      }
    );
  }

  if (isDataSyncing) {
    return (
      <div className={styles.addTrip}>
        <div className={styles.container}>
          <div className={styles.loadingRow}>
            <Spin size="large" /> {addTripEn.syncingTrips}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.addTrip}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Link to="/trips" className={styles.backLink}>
            <ArrowLeftOutlined /> {addTripEn.back}
          </Link>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{addTripEn.pageTitle}</h1>
            <Button
              type="primary"
              onClick={() => void attemptImport()}
              disabled={!canImport}
              loading={importing}
            >
              {addTripEn.importAction}
            </Button>
          </div>
        </div>

        <>
            <div className={styles.importHeader}>
              <h2 className={styles.importHeading}>{addTripEn.importHeading}</h2>
              <Button type="link" onClick={() => navigate('/trips/new/edit')}>
                {addTripEn.manualEntry}
              </Button>
            </div>
            <Collapse
              activeKey={activeKeys}
              onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys.map(String) : [String(keys)])}
              items={collapseItems}
              className={styles.collapse}
            />
            {selectedItineraryId ? (
              <div className={styles.tripTitleBlock}>
                <div className={styles.tripTitleHeading}>
                  {addTripEn.tripTitle}
                  <EditOutlined />
                </div>
                <Input
                  value={tripName}
                  onChange={(e) => setTripName(e.target.value)}
                  placeholder={addTripEn.enterTripName}
                />
              </div>
            ) : null}
            {mapped ? (
              <div className={styles.bottomActions}>
                <Button
                  className={styles.editBeforeSaving}
                  onClick={handleEditBeforeSaving}
                  disabled={importing || detailLoading}
                >
                  {addTripEn.editBeforeSaving}
                </Button>
                <Button
                  type="primary"
                  className={styles.bottomImport}
                  onClick={() => void attemptImport()}
                  disabled={!canImport}
                  loading={importing}
                >
                  {addTripEn.importAction}
                </Button>
              </div>
            ) : null}
        </>
      </div>

      <Modal
        title={addTripEn.duplicateTitle}
        open={duplicateOpen}
        onCancel={() => setDuplicateOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setDuplicateOpen(false)}>
            {addTripEn.cancel}
          </Button>,
          <Button
            key="new"
            danger
            onClick={() => {
              setDuplicateOpen(false);
              setAddAsNewConfirmOpen(true);
            }}
          >
            {addTripEn.addAsNew}
          </Button>,
          <Button key="replace" onClick={() => void performImport(true)}>
            {addTripEn.replaceExisting}
          </Button>,
          <Button key="open" type="primary" onClick={openExistingDuplicate}>
            {addTripEn.openExisting}
          </Button>,
        ]}
      >
        <p>
          {addTripEn.duplicateMessage(
            duplicateMatches.map((t) => t.title || tripDetailEn.untitled).join(', ')
          )}
        </p>
      </Modal>

      <Modal
        title={addTripEn.addAsNewConfirmTitle}
        open={addAsNewConfirmOpen}
        onCancel={() => setAddAsNewConfirmOpen(false)}
        okText={addTripEn.addAsNewConfirmOk}
        okButtonProps={{ danger: true }}
        cancelText={addTripEn.cancel}
        onOk={() => void performImport(false)}
      >
        <p>{addTripEn.addAsNewConfirmMessage}</p>
      </Modal>
    </div>
  );
};

const SectionLabel: React.FC<{ title: string; summary?: string | null }> = ({ title, summary }) => (
  <div className={styles.sectionLabel}>
    <span className={styles.sectionTitle}>{title}</span>
    {summary ? <span className={styles.sectionSummary}>{summary}</span> : null}
  </div>
);

export default AddTrip;
